import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  costFormater,
  List,
  Option,
  OptionKey,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { ICategoryDtoCategoryTypeEnum } from '@/dtos/models/ICategoryDto';
import { EmptyTransactionsListPlaceholder } from '@/modules/transaction/components/TransactionsList/EmptyTransactionsListPlaceholder';
import colors from '@/styles/colors';
import theme from '@/styles/theme';

import ServiceCategoryIcon from '../ServiceCategoryIcon';
import useLoadMore from '../SpendLimitsTable/hooks/useLoadMore';
import { LoadMoreButton } from '../SpendLimitsTable/LoadMoreButton';
import { SpendLimitsPeriodHeader } from '../SpendLimitsTable/SpendLimitsPeriodHeader';

import type { CategorySpendLimitRow } from './types';

interface CategorySpendLimitsTableProps {
  categories: CategorySpendLimitRow[];
  isLoading?: boolean;
  hideBackground?: boolean;
  showPeriodDropdown?: boolean;
  periodOptions?: Option[];
  selectedPeriod?: OptionKey;
  onPeriodChange?: (value: OptionKey) => void;
  isPeriodLoading?: boolean;
}

export const CategorySpendLimitsTable: React.FC<CategorySpendLimitsTableProps> = ({
  categories,
  isLoading,
  hideBackground,
  showPeriodDropdown,
  periodOptions = [],
  selectedPeriod,
  onPeriodChange,
  isPeriodLoading,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { displayedItems, showAll, hasMoreItems, remainingCount, toggleShowAll } =
    useLoadMore(categories);
  const hasData = useMemo(() => categories.length > 0, [categories.length]);

  return (
    <Box
      data-testid="CategorySpendLimitsTable"
      background={hideBackground ? undefined : 'module'}
      round={hideBackground ? undefined : 'container1Round'}
      pad={hideBackground ? undefined : 'l'}
      margin={{ top: 'l' }}
    >
      <SpendLimitsPeriodHeader
        title={`${t('Service category spend limits')}: ${categories?.length || '0'}`}
        showPeriodDropdown={showPeriodDropdown}
        periodOptions={periodOptions}
        selectedPeriod={selectedPeriod}
        onPeriodChange={onPeriodChange}
        isPeriodLoading={isPeriodLoading}
        hideBackground={hideBackground}
        testIdPrefix="category-spend-limits"
      />
      {isMobile ? (
        <Box direction="column" gap="spacing12">
          {isLoading ? (
            <List.Preloader testId="category-spend-limits-preloader" count={1} />
          ) : (
            <>
              <Box direction="column" gap="spacing8" style={{ position: 'relative' }}>
                {!hasData && <EmptyTransactionsListPlaceholder title={t('Category Spend Limits')} />}
                {displayedItems.map((category) => (
                  <Box
                    key={category.id}
                    data-testid={`category-spend-limit-item-${category.id}`}
                    border={{ side: 'all', size: 'small', color: 'border2' }}
                    background="canvas"
                    round="container2Round"
                    pad="spacing16"
                    gap="spacing8"
                  >
                    <Box direction="row" align="center" gap="spacing8">
                      <ServiceCategoryIcon
                        type={category.categoryType as ICategoryDtoCategoryTypeEnum}
                        size="24px"
                        color={colors.activity[category.categoryType as ICategoryDtoCategoryTypeEnum]}
                      />
                      <Text weight="bold" size="large" color="textBody">
                        {category.categoryLabel}
                      </Text>
                    </Box>
                    <Box
                      alignSelf="stretch"
                      height="1px"
                      background="border1"
                    />
                    <Box direction="row" justify="between">
                      <Text color="textSecondary" weight={500} size="small">
                        {t('Spend limit')}
                      </Text>
                      <Text color="textBody" size="small" weight={500}>
                        {costFormater(category.categorySpendLimit, true)}
                      </Text>
                    </Box>
                    <Box direction="row" justify="between">
                      <Text color="textSecondary" weight={500} size="small">
                        {t('Total spent')}
                      </Text>
                      <Text color="textBody" size="small" weight={500}>
                        {costFormater(category.totalSpent, true)}
                      </Text>
                    </Box>
                  </Box>
                ))}
                {hasMoreItems && !showAll && (
                  <Box
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '120px',
                      background: theme.colors.fade1,
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </Box>
              {hasMoreItems && (
                <Box margin={{ top: 'spacing16' }}>
                  <LoadMoreButton
                    isExpanded={showAll}
                    remainingCount={remainingCount}
                    onClick={toggleShowAll}
                    isPending={isLoading}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      ) : (
        <List>
          <List.Header style={{ border: `1px solid ${theme.colors.legend}` }}>
            <List.Header.Item flex={5}>{t('Category')}</List.Header.Item>
            <List.Header.Item flex={2.5} margin={{ left: 's' }}>
              {t('Spend limit')}
            </List.Header.Item>
            <List.Header.Item flex={2.5}>{t('Total spent')}</List.Header.Item>
          </List.Header>

          {isLoading ? (
            <List.Preloader testId="category-spend-limits-preloader" count={1} />
          ) : (
            <>
              <Box style={{ position: 'relative' }}>
                {!hasData && <EmptyTransactionsListPlaceholder title={t('Category Spend Limits')} />}
                {displayedItems.map((category) => (
                  <List.Item key={category.id} disableHover data-testid={`category-spend-limit-item-${category.id}`}>
                    <Box style={{ flex: 5 }} direction="row" align="center" gap="spacing8">
                      <ServiceCategoryIcon
                        type={category.categoryType as ICategoryDtoCategoryTypeEnum}
                        size="24px"
                        color={colors.activity[category.categoryType as ICategoryDtoCategoryTypeEnum]}
                      />
                      <Text weight="bold">{category.categoryLabel}</Text>
                    </Box>
                    <List.Item.Text flex={2.5} weight={400} style={{ marginLeft: theme.spacings.s }}>
                      {costFormater(category.categorySpendLimit, true)}
                    </List.Item.Text>
                    <List.Item.Text flex={2.5} weight={400}>
                      {costFormater(category.totalSpent, true)}
                    </List.Item.Text>
                  </List.Item>
                ))}
                {hasMoreItems && !showAll && (
                  <Box
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '120px',
                      background: theme.colors.fade1,
                    }}
                  />
                )}
              </Box>
              {hasMoreItems && (
                <Box pad={{ vertical: 'spacing16' }}>
                  <LoadMoreButton
                    isExpanded={showAll}
                    remainingCount={remainingCount}
                    onClick={toggleShowAll}
                    isPending={isLoading}
                  />
                </Box>
              )}
            </>
          )}
        </List>
      )}
    </Box>
  );
};
